import { regionCode } from '../regions.js';

export type SequenceApplicationIdentifierField = 'NdaNumber' | 'DossierId' | 'EuApplicationUuid';

/**
 * The regulator's application identity for collision checks. Choose exactly one
 * regional field: a control/transaction number cannot identify a different app.
 * Missing identity stays unknown; do not guess from another region's fields.
 * This does not change the legacy dashboard/sibling grouping predicate above.
 */
export function applicationIdentifierSpec(
  region: string | null | undefined,
): { field: SequenceApplicationIdentifierField; label: string } | null {
  const code = regionCode(region);
  let field: SequenceApplicationIdentifierField;
  let label: string;
  switch (code) {
    case 'FDA': field = 'NdaNumber'; label = 'FDA application'; break;
    case 'HC': field = 'DossierId'; label = 'Dossier ID'; break;
    case 'TGA': field = 'DossierId'; label = 'eSubmission ID'; break;
    case 'EMA':
    case 'MHRA': field = 'EuApplicationUuid'; label = 'EU application UUID'; break;
    case 'PMDA':
    case 'Swissmedic': field = 'NdaNumber'; label = 'application number'; break;
    default: return null;
  }
  return { field, label };
}

export function applicationSequenceIdentifier(
  region: string | null | undefined,
  values: Readonly<Record<string, unknown>>,
): { field: SequenceApplicationIdentifierField; value: string; label: string } | null {
  const spec = applicationIdentifierSpec(region);
  if (!spec) return null;
  const { field, label } = spec;
  const raw = values[field];
  const value = typeof raw === 'string' ? raw.trim() : '';
  return value ? { field, value, label } : null;
}
